/* RESPONSIBLE TEAM: team-reporting */
import { inject as service } from '@ember/service';
import Helper from '@ember/component/helper';
import type Session from 'embercom/services/session';
import type { AdminPermissions } from 'embercom/objects/inbox/admin-with-permissions';

interface PermissionsService {
  currentAdminCan(permission: string): boolean;
}

// Map snake_case permission keys to camelCase AdminPermissions properties
const PERMISSION_KEY_MAP: Record<string, keyof AdminPermissions> = {
  can_use_data_connectors: 'canUseDataConnectors',
  can_manage_teammates: 'canManageTeammates',
  can_manage_tags: 'canManageTags',
  can_manage_saved_replies: 'canManageMacros',
  can_create_macros: 'canCreateMacros',
  can_edit_macros: 'canEditMacros',
  can_delete_macros: 'canDeleteMacros',
  can_access_copilot: 'canInboxAccessCopilot',
  can_manage_inbox_views: 'canManageInboxViews',
  can_reassign_conversations: 'canReassignConversations',
  can_redact_conversation_parts: 'canRedactConversationParts',
  can_redact_conversation_replies: 'canRedactConversationReplies',
  can_redact_conversation_notes: 'canRedactConversationNotes',
  can_access_workspace_settings: 'canAccessWorkspaceSettings',
  can_access_contacts: 'canAccessContacts',
  can_access_billing_settings: 'canAccessBillingSettings',
  can_use_personal_macros: 'canUsePersonalMacros',
};

type HasPermissionSignature = {
  Args: {
    Positional: [string];
  };
  Return: boolean;
};

export default class HasPermissionHelper extends Helper<HasPermissionSignature> {
  @service declare permissionsService: PermissionsService;
  @service declare session: Session;

  compute([permissionName]: [string]): boolean {
    // For Inbox 2, we don't have the app service. Instead, we have a
    // lightweight session service with teammate permissions which we can query.
    if (
      this.session?.isWorkspaceLoaded &&
      this.session?.isTeammateLoaded &&
      this.session?.teammate?.permissions
    ) {
      let camelCaseKey = PERMISSION_KEY_MAP[permissionName] ?? permissionName;
      let permissions = this.session.teammate.permissions;
      return Boolean((permissions as any)[camelCaseKey]);
    }

    // For the rest of the app, use the permissions service
    return Boolean(this.permissionsService.currentAdminCan(permissionName));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'has-permission': typeof HasPermissionHelper;
  }
}
